import { isNil } from "lodash";

export const formatNumber = (value: string | number) => {
  const n = +value;
  return Number.isFinite(n)
    ? n.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    : "--";
};
export const formatNumberWithPadding = (value: string | number, len = 2) => {
  const n = +value;

  if (typeof n !== "number" || isNaN(n)) {
    return "--";
  }

  const valueStr = `${n}`;
  return valueStr.length >= len
    ? valueStr
    : new Array(len - valueStr.length + 1).join("0") + valueStr;
};
export const formatAsPercent = (
  value: string | number,
  precision: number = 2,
  { showBelowThreshold = false }: { showBelowThreshold?: boolean } = {}
) => {
  const n = +value;

  if (typeof n !== "number" || isNaN(n) || n === Infinity || n === -Infinity) {
    return "--";
  }

  if (showBelowThreshold && n !== 0 && Number(n.toFixed(precision)) === 0) {
    const threshold = (1 / 10 ** precision).toFixed(precision);
    return n > 0 ? `<${threshold}%` : `>-${threshold}%`;
  }

  return `${n.toFixed(precision)}%`;
};

const LARGE_NUMBER_SCALES: Array<{ value: number; suffix: string }> = [
  { value: 1e15, suffix: "Qd" },
  { value: 1e12, suffix: "T" },
  { value: 1e9, suffix: "B" },
  { value: 1e6, suffix: "M" }
];

/**
 * Formats numbers at or above one million as "x.xx M", "x.xx B", or
 * "x.xx T". This keeps the value inside a fixed-width UI slot, for
 * example a metric card.
 */
export const formatAsLargeNumber = (value: string | number): string => {
  const n = +value;
  if (!Number.isFinite(n)) {
    return "--";
  }

  const scale = LARGE_NUMBER_SCALES.find(
    ({ value: threshold }) => Math.abs(n) >= threshold
  );

  return scale
    ? `${(n / scale.value).toFixed(2)} ${scale.suffix}`
    : formatNumber(n);
};

export const isValidNumber = (n: any): n is number => Number.isFinite(n);

export const getGcd = (a: number, b: number): number => {
  const formatA = Math.abs(Math.floor(a));
  const formatB = Math.abs(Math.floor(b));
  if (b === 0) {
    return formatA;
  }
  return getGcd(formatB, formatA % formatB);
};

export const getAspectRatio = (width: number, height: number) => {
  const gcd = getGcd(width, height);
  const aspectRatioWidth = width / gcd;

  const aspectRatioHeight = height / gcd;

  return {
    aspectRatioWidth,
    aspectRatioHeight
  };
};

const getNumberOfZerosAfterDecimalPlace = (value: number): number => {
  const str = value.toString();

  let numberOfZeros = 0;
  if (str.includes(".")) {
    const fractional = str.split(".")[1];

    for (let index = 0; index < fractional.length; index++) {
      if (+fractional[index] > 0) {
        break;
      }
      // eslint-disable-next-line no-plusplus
      numberOfZeros++;
    }
  }

  return numberOfZeros;
};

export const formatToNearestPrecision = (
  value: number,
  displayPrecisionAfterZeros: number = 2
): string =>
  value.toFixed(
    displayPrecisionAfterZeros + getNumberOfZerosAfterDecimalPlace(value)
  );

export const formatNumberToPrecision = ({
  value,
  precision = 2,
  wholeNumberWithoutPrecision,
  asPercent
}: {
  value?: string | number;
  precision?: number;
  wholeNumberWithoutPrecision?: boolean;
  asPercent?: boolean;
}): string => {
  const numberValue = Number(value);

  if (typeof value === "string" && Number.isNaN(numberValue)) {
    // when value is a string
    return value;
  }

  if (isNil(value) || Number.isNaN(numberValue)) {
    return "--";
  }

  // precision 0 if value does not have decimals and 2 when they do
  const toPrecision =
    wholeNumberWithoutPrecision && Number(numberValue.toFixed(2)) % 1 === 0
      ? 0
      : precision;

  const formattedNumber = Number(
    numberValue.toFixed(toPrecision)
  ).toLocaleString("en-US", {
    minimumFractionDigits: toPrecision,
    maximumFractionDigits: toPrecision
  });

  return asPercent ? `${formattedNumber}%` : formattedNumber;
};

/**
 * Formats a number with an ordinal suffix (e.g. 1st, 2nd, 3rd, 4th, 5th, etc.)
 * @param num The number to format
 * @returns The number with an ordinal suffix (e.g. 1st, 2nd, 3rd, 4th, 5th, etc.)
 */
export const formatWithOrdinalSuffix = (num: number): string => {
  // Special cases for 11th, 12th, 13th
  if (num % 100 >= 11 && num % 100 <= 13) {
    return `${num}th`;
  }

  const suffixes: Record<number, string> = { 1: "st", 2: "nd", 3: "rd" };
  const suffix = suffixes[num % 10] || "th";

  return `${num}${suffix}`;
};
